import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { CONFIDENCE_TIERS } from '../lib/palette.js';
import { fillFor, patternDefs } from '../lib/patterns.js';
import { withFormats } from '../lib/table.js';
import {
  SOURCE_FLOW_GROUPINGS,
  SOURCE_FLOW_ORDERS,
  incident,
  sankeyLayout,
  sourceFlowLinkColumns,
  sourceFlowLinkRows,
  sourceFlowModel,
  sourceFlowRoleColumns,
  sourceFlowRoleRows,
  sourceFlowSummary,
} from '../lib/flow.js';
import { hours as formatHours, percent, longDate, count } from '../lib/format.js';

/** The shared filters this view reads. The granularity, the order and the floor are its own. */
const PARAM_KEYS = Object.freeze(['run_id', 'dimension', 'entity', 'from', 'weeks', 'pipeline']);

/** The diagram's own coordinate space. Fixed, so the chart scales with the card. */
const PLOT = Object.freeze({
  width: 760,
  block: 12,
  gap: 7,
  rowHeight: 34,
  minHeight: 260,
  top: 20,
  /** The room the two columns of names need, inside the width. */
  leftGutter: 168,
  rightGutter: 210,
  label: 24,
});

/** How far a mark that is not in the isolation fades. Interaction state, never an encoding. */
const DIMMED = 0.16;

const GROUPING_LABELS = { mode: 'Source modes', layer: 'Ribbon layers' };
const ORDER_LABELS = { hours: 'Hours', explicit: 'Least known', name: 'Name' };
/** The thresholds the "hide under" control offers, in hours over the window. */
const FLOORS = [0, 1, 8, 40, 100];

const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const exact = (v) => formatHours(v, { exact: true });

/**
 * The source flow (Design Review V-11; TRD 10.2; Phase 45): how much of each role's demand is
 * known, and how much the run made up.
 *
 * The quality metrics above this card answer that for the company in one percentage, and the
 * ribbon answers it for the company week by week. Neither says that development is 80 per cent
 * explicit and strategy 70 per cent inferred — which is the form of the answer a planner can
 * act on, because it says whose number to trust and whose to go and fix.
 *
 * Left nodes are the run's source modes, right nodes are roles, and a band is the hours one mode
 * put on one role over the window. The modes partition the allocation rows, so both columns add
 * to the same total and a role's total is the ribbon filtered to that role. Hue is the ribbon's
 * **layer** and fill is its confidence, so nothing here is a second colour vocabulary: two modes
 * of one layer are the same colour and are told apart by their labels and by position.
 *
 * Every number comes from `GET /forecast/source-flow` and every position from `lib/flow.js`,
 * which the CSV export imports too. This component formats, places nothing of its own, and
 * computes no hours.
 */
class SourceFlow extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _grouping: { state: true },
    _order: { state: true },
    _floor: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _pinned: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._grouping = 'mode';
    this._order = 'hours';
    this._floor = 0;
    this._q = '';
    this._hover = null;
    this._pinned = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      .field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .field select,
      .field input {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        padding: 3px 9px;
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--brand);
        cursor: pointer;
        padding: 0;
        text-decoration: underline;
      }
      svg.plot {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
      }
      .band {
        opacity: 0.62;
        cursor: pointer;
        transition: opacity 120ms ease-out;
      }
      .band-g[data-dim='true'] .band,
      .node-g[data-dim='true'] .block,
      .node-g[data-dim='true'] .label,
      .node-g[data-dim='true'] .meta {
        opacity: ${DIMMED};
      }
      .band-g[data-lit='true'] .band {
        opacity: 0.92;
      }
      .block {
        cursor: pointer;
      }
      text {
        font-size: 11.5px;
        fill: var(--ink);
      }
      text.meta {
        font-size: 10.5px;
        fill: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      text.axis {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        fill: var(--ink-muted);
      }
      .legends {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 22px;
        margin: 10px 0 2px;
      }
      ul.legend {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 14px;
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 11.5px;
        color: var(--ink-muted);
      }
      ul.legend li {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }
      .sw {
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
      }
      p.note {
        margin: 8px 0 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--ink-muted);
        max-width: 92ch;
      }
      .state {
        padding: 22px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-danger);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      data-table {
        display: block;
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Data

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  _params() {
    const { run } = this.appState;
    if (!run) return null;
    return apiParams(this.appState.filters, PARAM_KEYS);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/source-flow', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    if (!this._data) return null;
    const key = `${this._grouping}|${this._order}|${this._floor}|${this._q}`;
    if (this._modelFor !== this._data || this._modelKey !== key) {
      this._model_ = sourceFlowModel(this._data, {
        grouping: this._grouping,
        order: this._order,
        minHours: this._floor,
        q: this._q,
      });
      this._modelFor = this._data;
      this._modelKey = key;
    }
    return this._model_;
  }

  /** What is lit: what the cursor is on, else what the reader pinned. */
  get _focus() {
    return this._hover ?? this._pinned;
  }

  _pin(key) {
    this._pinned = this._pinned === key ? null : key;
  }

  _show(event, title, rows, footer) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title,
      rows: rows.filter(Boolean),
      footer: footer || '',
    });
  }

  _hide() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  _showSource(event, model, node) {
    this._show(
      event,
      node.label,
      [
        { label: 'Hours in the window', value: exact(node.hours) },
        { label: 'Share of all demand', value: percent(node.share) },
        { label: 'Roles it reaches', value: count(node.flows) },
        node.hours !== node.response_hours
          ? { label: 'Before the controls hid a role', value: exact(node.response_hours) }
          : null,
      ],
      node.note,
    );
  }

  _showRole(event, model, role) {
    this._show(
      event,
      role.label,
      [
        { label: 'Hours in the window', value: exact(role.hours) },
        {
          label: 'Explicit',
          value: `${exact(role.explicit_hours)} · ${percent(role.explicit_share)}`,
        },
        {
          label: 'Inferred',
          value: `${exact(role.inferred_hours)} · ${percent(role.inferred_share)}`,
        },
        role.recurring_hours > 0
          ? { label: 'Recurring baseline', value: exact(role.recurring_hours) }
          : null,
        role.nonproject_hours > 0
          ? { label: 'Non-project', value: exact(role.nonproject_hours) }
          : null,
        role.pipeline_hours > 0 ? { label: 'Pipeline', value: exact(role.pipeline_hours) } : null,
        { label: 'People', value: count(role.people) },
      ],
      `explicit of the committed hours alone: ${percent(role.explicit_of_committed)}`,
    );
  }

  _showBand(event, model, flow) {
    const role = model.roles.find((r) => r.key === flow.role);
    const source = model.sources.find((s) => s.key === flow.source);
    this._show(
      event,
      `${flow.source_label} → ${flow.role_label}`,
      [
        { label: 'Hours', value: exact(flow.hours) },
        { label: `Of ${flow.role_label}`, value: percent(flow.hours / (role?.hours || 1)) },
        { label: `Of ${flow.source_label}`, value: percent(flow.hours / (source?.hours || 1)) },
      ],
      flow.source_modes.length > 1
        ? `${plural(flow.source_modes.length, 'source mode')} folded into this layer`
        : (source?.note ?? ''),
    );
  }

  // -------------------------------------------------------------------------------------------
  // The diagram

  _plot(model) {
    const rows = Math.max(model.sources.length, model.roles.length);
    const height = Math.max(PLOT.minHeight, rows * PLOT.rowHeight);
    const inner = PLOT.width - PLOT.leftGutter - PLOT.rightGutter;
    const layout = sankeyLayout(model, {
      width: inner,
      height,
      gap: PLOT.gap,
      blockWidth: PLOT.block,
      top: PLOT.top,
    });
    const lit = incident(model, this._focus);
    const total = layout.height + 26;
    const litBand = (key) => !lit || lit.flows.has(key);
    const dimNode = (key) => Boolean(lit) && !lit.nodes.has(key);

    return svg`
      <svg
        class="plot"
        viewBox="0 0 ${PLOT.width} ${total}"
        role="img"
        aria-label=${`${plural(layout.sources.length, 'source')} of demand across ${plural(
          layout.roles.length,
          'role',
        )}`}
        @pointerleave=${() => {
          this._hover = null;
          this._hide();
        }}
      >
        ${unsafeSVG(patternDefs())}
        <g class="axis">
          <text class="axis" x="0" y="10">Where the hours came from</text>
          <text class="axis" x=${PLOT.width} y="10" text-anchor="end">Role</text>
        </g>
        <g transform=${`translate(${PLOT.leftGutter} 0)`}>
          ${repeat(
            layout.bands,
            (b) => b.key,
            (b) => svg`<g
              class="band-g"
              data-key=${b.key}
              data-dim=${!litBand(b.key)}
              data-lit=${Boolean(lit) && litBand(b.key)}
            >
              <path
                class="band mark"
                role="button"
                tabindex="0"
                d=${b.path}
                fill=${fillFor(b.flow.token, b.flow.fill)}
                stroke=${`var(${b.flow.token})`}
                stroke-width="0.5"
                @pointerenter=${(e) => {
                  this._hover = b.key;
                  this._showBand(e, model, b.flow);
                }}
                @pointermove=${(e) => this._showBand(e, model, b.flow)}
                @focus=${() => (this._hover = b.key)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(b.key)}
                @keydown=${(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this._pin(b.key);
                  }
                }}
              >
                <title>
                  ${b.flow.source_label} → ${b.flow.role_label}: ${exact(b.flow.hours)}
                </title>
              </path>
            </g>`,
          )}
          ${repeat(
            layout.sources,
            (b) => b.key,
            (b) => svg`<g class="node-g" data-key=${`s:${b.key}`} data-dim=${dimNode(`s:${b.key}`)}>
              <rect
                class="block mark"
                role="button"
                tabindex="0"
                x="0"
                y=${b.top}
                width=${PLOT.block}
                height=${b.height}
                rx="2"
                fill=${fillFor(b.row.token, b.row.fill)}
                stroke=${`var(${b.row.token})`}
                @pointerenter=${(e) => {
                  this._hover = `s:${b.key}`;
                  this._showSource(e, model, b.row);
                }}
                @focus=${() => (this._hover = `s:${b.key}`)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(`s:${b.key}`)}
              ></rect>
              <text class="label" x="-8" y=${b.top + b.height / 2 - 4} text-anchor="end">
                ${b.row.label}
              </text>
              <text class="meta" x="-8" y=${b.top + b.height / 2 + 9} text-anchor="end">
                ${exact(b.row.hours)} · ${percent(b.row.share)}
              </text>
            </g>`,
          )}
          ${repeat(
            layout.roles,
            (b) => b.key,
            (b) => svg`<g class="node-g" data-key=${`r:${b.key}`} data-dim=${dimNode(`r:${b.key}`)}>
              <rect
                class="block mark"
                role="button"
                tabindex="0"
                x=${b.x}
                y=${b.top}
                width=${PLOT.block}
                height=${b.height}
                rx="2"
                fill="var(--ink-strong)"
                @pointerenter=${(e) => {
                  this._hover = `r:${b.key}`;
                  this._showRole(e, model, b.row);
                }}
                @focus=${() => (this._hover = `r:${b.key}`)}
                @blur=${() => (this._hover = null)}
                @click=${() => this._pin(`r:${b.key}`)}
              ></rect>
              <text class="label" x=${b.x + PLOT.block + 8} y=${b.top + b.height / 2 - 4}>
                ${b.row.label}
              </text>
              <text class="meta" x=${b.x + PLOT.block + 8} y=${b.top + b.height / 2 + 9}>
                ${exact(b.row.hours)} · ${percent(b.row.explicit_share)} explicit
              </text>
            </g>`,
          )}
        </g>
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    const group = (options, current, onPick, label) => {
      const slug = label.toLowerCase();
      return html`
        <span class="segmented">
          <span>${label}</span>
          <span class="group" role="group" aria-label=${label}>
            ${options.map(
              ([key, text]) => html`
                <button
                  type="button"
                  data-control=${slug}
                  data-key=${key}
                  aria-pressed=${current === key ? 'true' : 'false'}
                  @click=${() => onPick(key)}
                >
                  ${text}
                </button>
              `,
            )}
          </span>
        </span>
      `;
    };
    return html`
      <div class="controls">
        ${group(
          SOURCE_FLOW_GROUPINGS.map((g) => [g, GROUPING_LABELS[g]]),
          this._grouping,
          (k) => (this._grouping = k),
          'Left',
        )}
        ${group(
          SOURCE_FLOW_ORDERS.map((o) => [o, ORDER_LABELS[o]]),
          this._order,
          (k) => (this._order = k),
          'Order',
        )}
        <label class="field">
          Hide under
          <select @change=${(e) => (this._floor = Number(e.target.value))}>
            ${FLOORS.map(
              (f) =>
                html`<option value=${f} ?selected=${this._floor === f}>
                  ${f === 0 ? 'nothing' : `${f} h`}
                </option>`,
            )}
          </select>
        </label>
        <label class="field">
          Find
          <input
            type="search"
            placeholder="role"
            .value=${this._q}
            @input=${(e) => (this._q = e.target.value)}
          />
        </label>
        ${
          this._pinned
            ? html`<button class="link" type="button" @click=${() => (this._pinned = null)}>
                Clear the isolation
              </button>`
            : nothing
        }
      </div>
    `;
  }

  _kpis(summary) {
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${percent(summary.explicit_share)}</div>
          <div class="l">of the demand is explicit</div>
        </div>
        <div class="kpi">
          <div class="n">${exact(summary.inferred_hours)}</div>
          <div class="l">hours the run inferred</div>
        </div>
        <div class="kpi">
          <div class="n">
            ${summary.least_known ? percent(summary.least_known.explicit_share) : '—'}
          </div>
          <div class="l">${summary.least_known ? summary.least_known.label : 'no roles'}</div>
        </div>
        <div class="kpi">
          <div class="n">${count(summary.roles)}</div>
          <div class="l">roles drawn</div>
        </div>
      </div>
    `;
  }

  _legend(model) {
    const seen = new Map();
    for (const s of model.sources) if (!seen.has(s.layer)) seen.set(s.layer, s);
    return html`
      <div class="legends">
        <ul class="legend">
          ${[...seen.values()].map(
            (s) =>
              html`<li>
                <span class="sw" style=${`background: var(${s.token})`}></span>${s.layer_label}
              </li>`,
          )}
        </ul>
        <ul class="legend">
          ${CONFIDENCE_TIERS.map(
            (t) =>
              html`<li>
                <span
                  class="sw"
                  style=${`background: ${fillFor('--type-1', t.fill)}`}
                  aria-hidden="true"
                ></span>
                ${t.label} confidence${t.marker ? ` ${t.marker}` : ''}
              </li>`,
          )}
          <li>Hue is the ribbon's layer; two modes of one layer share it.</li>
        </ul>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const where = model.entity ? ` · ${model.entity.label}` : '';
    return `${longDate(model.from)} to ${longDate(model.to)}${where} · ${plural(
      model.weeks_count,
      'week',
    )}`;
  }

  _notes(model, summary) {
    const notes = [];
    notes.push(
      html`<p class="note">
        A band is the hours one source put on one role over the window, and the two columns hold the
        same hours: every allocation row of the run is in exactly one band.
        <strong>Percent explicit</strong> is a role's explicit hours over its total — the same
        reading as the quality metrics above, one role at a time — so it counts the non-project
        baseline and the weighted pipeline in the denominator, because those are in the number on
        the heatmap too. The table carries the other reading, explicit over the committed hours
        alone.
      </p>`,
    );

    notes.push(
      html`<p class="note" data-note="reconcile">
        A role's total is the demand ribbon filtered to that role over the same window: the two read
        the same rows with the same key. If they ever disagree, one of them is wrong.
      </p>`,
    );

    if (model.grouping === 'mode')
      notes.push(
        html`<p class="note" data-note="grouping">
          Two modes of one layer — an inferred schedule and an inferred role mix — are the same hue
          and sit together, because hue here is the ribbon's layer and not the mode. Fold the left
          column to <strong>Ribbon layers</strong> to read this card against the ribbon node for
          node.
        </p>`,
      );

    if (summary.least_known && summary.least_known.explicit_share < 0.5)
      notes.push(
        html`<p class="note" data-note="least-known">
          ${summary.least_known.label} is the least known role on this window:
          ${percent(summary.least_known.explicit_share)} of its ${exact(summary.least_known.hours)}
          came from a named assignment, and ${exact(summary.least_known.inferred_hours)} the run
          inferred. The percentage is a ratio and not a size — a four-hour role that is entirely
          inferred is still four hours — which is why the hours are printed beside it.
        </p>`,
      );

    if (summary.unmapped_hours > 0)
      notes.push(
        html`<p class="note" data-note="unmapped">
          ${exact(summary.unmapped_hours)} sit on <strong>unmapped</strong>: real hours whose
          service nobody has mapped to a canonical value yet. They are a role here because leaving
          them out would make every other role's share of the total wrong; the hygiene worklist
          above is where the mapping gets fixed.
        </p>`,
      );

    if (model.nonproject_mode === 'deduct')
      notes.push(
        html`<p class="note" data-note="deduct">
          This run is in <strong>deduct</strong> mode, so the non-project baseline is not demand at
          all — it came off the capacity line instead and has no band here. In demand mode it is a
          source like any other.
        </p>`,
      );

    if (summary.hidden_roles > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          The controls are hiding ${plural(summary.hidden_roles, 'role')} and
          ${exact(summary.hidden_hours)} with them. Each source block is as tall as the bands still
          drawn, so hiding a role shortens the left column too; every role's own percent explicit is
          about the run and does not follow what is on screen, and the table carries them all.
        </p>`,
      );

    return html`${notes}`;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _hourFormats() {
    return {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : exact(v)),
        percent: (v) => percent(v),
      },
    };
  }

  _tables(model) {
    const sub = this._subtitle(model);
    return html`
      <details
        class="table"
        ?open=${this._tableOpen}
        @toggle=${(e) => (this._tableOpen = e.target.open)}
      >
        <summary>Table view</summary>
        <data-table
          caption=${`The roles, and how much of each is known — ${sub}`}
          export-name="source-flow-roles"
          page-size="25"
          dense
          .columns=${withFormats(sourceFlowRoleColumns(), this._hourFormats)}
          .rows=${sourceFlowRoleRows(model)}
        ></data-table>
        <data-table
          caption=${`Every flow — ${sub}`}
          export-name="source-flow-links"
          page-size="25"
          dense
          .columns=${withFormats(sourceFlowLinkColumns(), this._hourFormats)}
          .rows=${sourceFlowLinkRows(model)}
        ></data-table>
      </details>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? sourceFlowSummary(model) : null;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!this._data && this._loading)
      body = html`<div class="state">Reading where the hours came from…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else if (!model.flows.length)
      body = html`
        ${this._controls()}
        <div class="state" data-note="empty">
          No allocated hours in this window, so there is nothing to trace back to a source.
        </div>
      `;
    else
      body = html`
        ${this._controls()} ${this._plot(model)} ${this._legend(model)}
        ${this._notes(model, summary)} ${this._tables(model)}
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Source flow</div>
            <h2>How much of each role's demand is known?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${summary ? this._kpis(summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('source-flow', SourceFlow);
